import VideoCam from '@material-ui/icons/Videocam';
import VideocamOff from '@material-ui/icons/VideocamOff';
import { IconButton } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import useDevices from '../../hooks/useDevices';

import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import React, { useRef, useState } from 'react';
import styles from './styles.js';
import { UserContext } from '../../context/UserContext';
import HlsFilterButton from '../HlsFilterComponent'; // Import the HlsFilterButton

export default function CombineVideoButton({
  classes,
  hasVideo,
  toggleVideo,
  getVideoSource,
  cameraPublishing,
  changeVideoSource,
  Publisher, // Pass the Publisher prop to use in HlsFilterButton
}) {
  const title = hasVideo ? 'Disable Camera' : 'Enable Camera';
  const { deviceInfo } = useDevices();
  const [devicesAvailable, setDevicesAvailable] = useState(null);
  const [options, setOptions] = useState(['Camera Source', 'Filter']);
  const [open, setOpen] = useState(false);
  const [hoverCameraSource, setHoverCameraSource] = useState(false);
  const anchorRef = useRef(null);
  const cameraSourceRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const localClasses = styles();
  const { user } = React.useContext(UserContext);
  const [filterModalOpen, setFilterModalOpen] = useState(false); // State for controlling filter modal

  React.useEffect(() => {
    setDevicesAvailable(deviceInfo.videoInputDevices);
    if (cameraPublishing) {
      const currentDeviceId = getVideoSource()?.deviceId;
      const IndexOfSelectedElement = devicesAvailable?.indexOf(
        devicesAvailable?.find((e) => e.deviceId === currentDeviceId)
      );
      setSelectedIndex(IndexOfSelectedElement);
    }
  }, [cameraPublishing, getVideoSource, deviceInfo, devicesAvailable]);

  React.useEffect(() => {
    if (devicesAvailable) {
      setOptions(['Camera Source', 'Filter']);
    }
  }, [devicesAvailable]);

  const handleChangeVideoSource = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
    const videoDeviceId = devicesAvailable?.find(
      (device) => device.label === event.target.textContent
    ).deviceId;
    changeVideoSource(videoDeviceId);
    setHoverCameraSource(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    setHoverCameraSource(false);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setHoverCameraSource(false);
  };

  const handleFilterClick = () => {
    setFilterModalOpen(true); // Open the filter modal
    setOpen(false); // Close the dropdown menu
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
  };

  return (
    <>
      <ButtonGroup
        className={classes.groupButton}
        disableElevation
        variant='contained'
        ref={anchorRef}
        aria-label='split button'
      >
        <Tooltip title={title} aria-label='add'>
          <IconButton
            onClick={toggleVideo}
            edge='start'
            aria-label='videoCamera'
            size='small'
            className={`${classes.arrowButton} ${
              !hasVideo ? classes.disabledButton : ''
            }`}
          >
            {!hasVideo ? <VideocamOff /> : <VideoCam />}
          </IconButton>
        </Tooltip>
        <IconButton
          size='small'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label='select merge strategy'
          aria-haspopup='menu'
          onClick={handleToggle}
          className={classes.arrowButton}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 101 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu'>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      ref={option === 'Camera Source' ? cameraSourceRef : null}
                      selected={index === selectedIndex}
                      onMouseEnter={() =>
                        option === 'Camera Source' && setHoverCameraSource(true)
                      }
                      onMouseLeave={() => setHoverCameraSource(false)}
                      onClick={(event) =>
                        option === 'Filter'
                          ? handleFilterClick()
                          : handleChangeVideoSource(event, index)
                      }
                      classes={{
                        selected: localClasses.selected,
                        root: localClasses.root,
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Submenu for camera options */}
      <Popper
        open={hoverCameraSource && devicesAvailable?.length > 0}
        anchorEl={cameraSourceRef.current}
        placement='right-start'
        style={{ zIndex: 102 }}
        transition
        onMouseEnter={() => setHoverCameraSource(true)}
        onMouseLeave={() => setHoverCameraSource(false)}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener
                onClickAway={() => setHoverCameraSource(false)}
              >
                <MenuList>
                  {devicesAvailable?.map((device, index) => (
                    <MenuItem
                      key={device.deviceId}
                      selected={index === selectedIndex}
                      onClick={(event) => handleChangeVideoSource(event, index)}
                      classes={{
                        selected: localClasses.selected,
                        root: localClasses.root,
                      }}
                    >
                      {device.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Filter Modal */}
      <HlsFilterButton
        classes={classes}
        Publisher={Publisher}
        open={filterModalOpen}
        onClose={handleFilterModalClose}
      />
    </>
  );
}
